import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PlayCircle, CheckCircle, ArrowRight, FileText, Users, Video, MessageSquare, HelpCircle, Image, Lock, Edit, Trash2, Power, PowerOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import QuizModal from "@/components/QuizModal";

export default function KnowledgeHub() {
  const { toast } = useToast();
  const [selectedDay, setSelectedDay] = useState(1);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [isCodeOfConductOpen, setIsCodeOfConductOpen] = useState(false);
  const [isReferBuddyOpen, setIsReferBuddyOpen] = useState(false);
  const [isHelpdeskOpen, setIsHelpdeskOpen] = useState(false);
  const [isMCQOpen, setIsMCQOpen] = useState(false);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState<any>(null);
  const [currentModule, setCurrentModule] = useState<any>(null);
  const [mcqAnswers, setMcqAnswers] = useState<string[]>([]);
  const [isLockedModalOpen, setIsLockedModalOpen] = useState(false);
  const [lockedModuleInfo, setLockedModuleInfo] = useState<{current: any, previous: any} | null>(null);

  // Prevent selectedDay from being reset by data refetches
  const selectedDayRef = useRef(selectedDay);
  const isUserInteraction = useRef(false);

  // Update ref when selectedDay changes
  useEffect(() => {
    selectedDayRef.current = selectedDay;
  }, [selectedDay]);

  // Create a stable setSelectedDay function that preserves user choice
  const handleDayChange = (day: number) => {
    isUserInteraction.current = true;
    setSelectedDay(day);
    selectedDayRef.current = day;
    // Reset the flag after a short delay
    setTimeout(() => {
      isUserInteraction.current = false;
    }, 100);
  };

  // Function to check if a module is available (progressive unlocking)
  const isModuleAvailable = (module: any, allModules: any[], progress: any) => {
    console.log(`Checking availability for module: ${module.title}, day: ${module.day}, sequence: ${module.sequence}`);
    
    // Check if THIS module is already completed - completed modules are always available
    const moduleProgress = progress?.find((p: any) => p.moduleId === module.id);
    if (moduleProgress?.isCompleted) {
      console.log(`Module ${module.title} is already completed, allowing access`);
      return true;
    }
    
    // First module of each day is always available
    if (parseInt(module.sequence) === 1) {
      console.log(`Module ${module.title} is first in day ${module.day}, allowing access`);
      return true;
    }

    // For modules within the same day, check if previous module is completed
    const currentSequence = parseInt(module.sequence);
    const previousSequence = currentSequence - 1;
    
    console.log(`Looking for previous module: day ${module.day}, sequence ${previousSequence}`);
    
    const previousModule = allModules.find(m => 
      m.day === module.day && parseInt(m.sequence) === previousSequence
    );

    console.log(`Previous module for ${module.title}:`, previousModule);

    if (!previousModule) {
      console.log(`No previous module found for ${module.title}, allowing access`);
      return true; // If no previous module found, allow access
    }

    // Check if previous module is completed
    const previousProgress = progress?.find((p: any) => p.moduleId === previousModule.id);
    const isCompleted = previousProgress?.isCompleted || false;
    
    console.log(`Previous module ${previousModule.title} completion status:`, isCompleted);
    console.log(`Module ${module.title} availability:`, isCompleted);
    
    return isCompleted;
  };

  // Function to get the previous module that needs to be completed
  const getPreviousModule = (module: any, allModules: any[]) => {
    return allModules.find(m => 
      m.day === module.day && parseInt(m.sequence) === parseInt(module.sequence) - 1
    );
  };

  // Function to get appropriate icon for each module type
  const getModuleIcon = (module: any, isLocked: boolean = false) => {
    if (isLocked) {
      return <Lock className="text-servify-medium-grey h-8 w-8" />;
    }
    
    if (module.isCompleted) {
      return <CheckCircle className="text-white h-8 w-8" />;
    }
    
    // Return specific icons based on module type
    switch (module.mtype) {
      case "video":
        return <Video className="text-white h-8 w-8" />;
      case "image":
        return <Image className="text-white h-8 w-8" />;
      case "form":
        return <FileText className="text-white h-8 w-8" />;
      case "helpdesk":
        return <FileText className="text-white h-8 w-8" />;
      case "external":
        return <MessageSquare className="text-white h-8 w-8" />;
      default:
        return <PlayCircle className="text-white h-8 w-8" />;
    }
  };

  const { data: modules, isLoading } = useQuery({
    queryKey: ['/training-modules'],
  });

  const { data: progressData } = useQuery({
    queryKey: ['/progress'],
  }) as { data: any };

  // Extract progress array from the response
  const progress = progressData?.progress || [];

  const { data: user } = useQuery({
    queryKey: ['/auth/me'],
  }) as { data: any };

  // Check if user is admin
  const isAdmin = user?.user?.role === 'admin' || user?.role === 'admin';

  const { data: quizzesData } = useQuery({
    queryKey: ['/quizzes'],
  }) as { data: any };

  const queryClient = useQueryClient();

  // Auto-select first available day when modules load
  useEffect(() => {
    if (modules && (modules as any)?.modules) {
      const allMods = (modules as any).modules;
      const days = [...new Set(allMods.map((m: any) => m.day))]
        .filter((day): day is number => typeof day === 'number')
        .sort((a, b) => a - b);
      
      // If selectedDay doesn't have any modules, switch to first available day
      const hasModulesForSelectedDay = allMods.some((m: any) => m.day === selectedDay);
      if (!hasModulesForSelectedDay && days.length > 0 && !isUserInteraction.current) {
        setSelectedDay(days[0]);
      }
    }
  }, [modules]); // Only run when modules data changes

  // Mutation for updating progress
  const updateProgressMutation = useMutation({
    mutationFn: async (progressData: { moduleId: string; isCompleted: boolean; timeSpent?: number }) => {
      console.log("Updating progress:", progressData);
      const response = await apiRequest("POST", "/progress", progressData);
      const result = await response.json();
      console.log("Progress update response:", result);
      return result;
    },
    onSuccess: (data) => {
      console.log("Progress update successful:", data);
      // Invalidate and refetch all progress-related data
      queryClient.invalidateQueries({ queryKey: ['/progress'] });
      queryClient.invalidateQueries({ queryKey: ['/progress/overall'] });
      queryClient.invalidateQueries({ queryKey: ['/training-modules'] });
      
      // Show success toast for non-video modules
      if (currentModule && currentModule.type !== 'video') {
        toast({
          title: "Module Completed!",
          description: `${currentModule.title} has been marked as completed.`,
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: "Failed to update progress",
        variant: "destructive",
      });
    },
  });

  // Mutation for creating quiz attempts
  const createQuizAttemptMutation = useMutation({
    mutationFn: async (attemptData: { quizId: number; score: number; isPassed: boolean; answers: any }) => {
      const response = await apiRequest("POST", "/quiz-attempts", attemptData);
      return response.json();
    },
    onSuccess: (data) => {
      console.log("Quiz attempt created successfully:", data);
      // Invalidate and refetch quiz attempts data immediately
      queryClient.invalidateQueries({ queryKey: ['/quiz-attempts'] });
      queryClient.refetchQueries({ queryKey: ['/quiz-attempts'] });
      
      // Also invalidate progress to ensure everything is updated
      queryClient.invalidateQueries({ queryKey: ['/progress'] });
      queryClient.invalidateQueries({ queryKey: ['/progress/overall'] });
      
      toast({
        title: data.attempt.isPassed ? "Quiz Passed! 🎉" : "Quiz Completed",
        description: data.attempt.isPassed 
          ? `Congratulations! You scored ${data.attempt.score}%` 
          : `You scored ${data.attempt.score}%. Keep practicing!`,
        variant: data.attempt.isPassed ? "default" : "destructive",
      });
    },
    onError: (error: Error) => {
      console.error("Quiz attempt creation error:", error);
      toast({
        title: "Error",
        description: "Failed to submit quiz attempt",
        variant: "destructive",
      });
    },
  });

  // Mutation for deleting training modules (admin only)
  const deleteModuleMutation = useMutation({
    mutationFn: async (moduleId: string) => {
      const response = await apiRequest("DELETE", `/training-modules/${moduleId}`);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Module Deleted",
        description: "Training module has been successfully deleted.",
      });
      // Invalidate and refetch modules
      queryClient.invalidateQueries({ queryKey: ['/training-modules'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: "Failed to delete training module",
        variant: "destructive",
      });
    },
  });

  // Mutation for toggling module active status (admin only)
  const toggleModuleStatusMutation = useMutation({
    mutationFn: async ({ moduleId, isActive }: { moduleId: string; isActive: boolean }) => {
      const response = await apiRequest("PUT", `/training-modules/${moduleId}`, { isActive });
      return response.json();
    },
    onSuccess: (data, variables) => {
      toast({
        title: "Success",
        description: `Module ${variables.isActive ? 'activated' : 'deactivated'} successfully`,
      });
      // Invalidate and refetch modules
      queryClient.invalidateQueries({ queryKey: ['/training-modules'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: "Failed to update module status",
        variant: "destructive",
      });
    },
  });

  const handleVideoComplete = () => {
    console.log("Video completed for module:", currentModule);
    console.log("Current module details:", {
      id: currentModule?.id,
      title: currentModule?.title,
      startTime: currentModule?.startTime,
      currentTime: Date.now()
    });
    
    if (currentModule) {
      // Check if this module has a linked quiz (works for ALL modules, not just specific ones)
      const quizzesArray = Array.isArray(quizzesData) ? quizzesData : (quizzesData?.quizzes || []);
      const moduleId = typeof currentModule.id === 'string' ? parseInt(currentModule.id) : currentModule.id;
      const quiz = quizzesArray.find((q: any) => {
        const quizModuleId = typeof q.moduleId === 'string' ? parseInt(q.moduleId) : q.moduleId;
        return quizModuleId === moduleId;
      });
      
      if (quiz) {
        console.log(`Module "${currentModule.title}" has a linked quiz, showing quiz modal...`);
        setIsVideoOpen(false);
        setCurrentQuiz(quiz);
        setIsQuizModalOpen(true);
        return;
      }

      // If no quiz linked, mark module as completed
      const timeSpent = Math.floor((Date.now() - (currentModule.startTime || Date.now())) / 1000);
      console.log(`No quiz for "${currentModule.title}", marking as completed. Time spent: ${timeSpent}s`);
      
      updateProgressMutation.mutate({
        moduleId: currentModule.id,
        isCompleted: true,
        timeSpent: Math.max(timeSpent, 300),
      });

      toast({
        title: "Module Completed!",
        description: `${currentModule.title} has been marked as completed.`,
      });
    } else {
      console.log("No current module found");
    }
  };

  const handleMCQOptionToggle = (option: string) => {
    console.log("MCQ option toggled:", option);
    setMcqAnswers(prev => {
      const newAnswers = prev.includes(option) 
        ? prev.filter(item => item !== option)
        : [...prev, option];
      console.log("Updated MCQ answers:", newAnswers);
      return newAnswers;
    });
  };

  const handleMCQSubmit = () => {
    console.log("MCQ Submit clicked!");
    console.log("Current MCQ answers:", mcqAnswers);
    
    // Get the quiz data from the database
    const quizzesArray = Array.isArray(quizzesData) ? quizzesData : (quizzesData?.quizzes || []);
    const moduleId = typeof currentModule?.id === 'string' ? parseInt(currentModule.id) : currentModule?.id;
    const quiz = quizzesArray.find((q: any) => {
      const quizModuleId = typeof q.moduleId === 'string' ? parseInt(q.moduleId) : q.moduleId;
      return quizModuleId === moduleId;
    });
    
    if (!quiz) {
      console.error("Quiz not found for module:", currentModule?.id);
      toast({
        title: "Error",
        description: "Quiz data not found. Please try again.",
        variant: "destructive",
      });
      return;
    }
    
    // Get the question and correct answers from the database
    const question = quiz.questions[0]; // First question
    const correctAnswerIndices = question.correctAnswer; // Array of correct indices
    const correctAnswers = correctAnswerIndices.map((index: number) => question.options[index]);
    
    // Calculate score based on exact match
    // User gets 100% only if they select EXACTLY the correct answers, no more, no less
    const sortedMcqAnswers = [...mcqAnswers].sort();
    const sortedCorrectAnswers = [...correctAnswers].sort();
    
    const isCorrect = sortedMcqAnswers.length === sortedCorrectAnswers.length &&
      sortedMcqAnswers.every((answer, idx) => answer === sortedCorrectAnswers[idx]);
    
    const score = isCorrect ? 100 : 0;
    const isPassed = score >= quiz.passingScore;
    
    console.log("Is correct:", isCorrect);
    console.log("Score:", score);
    console.log("Correct answers:", correctAnswers);
    console.log("Selected answers:", mcqAnswers);
    console.log("Quiz data:", quiz);
    
    // Save quiz attempt to database using the actual quiz ID
    createQuizAttemptMutation.mutate({
      quizId: quiz.id,
      score: score,
      isPassed: isPassed,
      answers: mcqAnswers
    });
    
    if (isPassed) {
      console.log("MCQ passed, showing success toast");
      toast({
        title: "Quiz Passed! 🎉",
        description: `Congratulations! You scored ${score}% and passed the quiz.`,
      });
      
      // Mark module as completed in database
      if (currentModule) {
        console.log("Marking module as completed:", currentModule.id);
        updateProgressMutation.mutate({
          moduleId: currentModule.id,
          isCompleted: true,
          timeSpent: 600, // 10 minutes for quiz completion
        });
      }
    } else {
      console.log("MCQ failed, showing error toast");
      toast({
        title: "Quiz Failed",
        description: `You scored ${score}%. You selected incorrect answer(s). Please review and try again.`,
        variant: "destructive",
      });
      return; // Don't close modal if incorrect
    }
    
    console.log("Closing MCQ modal");
    // Close MCQ modal and reset
    setIsMCQOpen(false);
    setMcqAnswers([]);
    setCurrentModule(null);
  };

  const handleQuizComplete = (score: number, isPassed: boolean, answers: number[][]) => {
    console.log("=== Quiz Completion Started ===");
    console.log("Quiz completed with data:", { score, isPassed, answers, answersLength: answers.length });
    console.log("Current quiz:", currentQuiz);
    console.log("Current module:", currentModule);
    
    // For admin users, just close the modal
    if (isAdmin) {
      console.log("Admin user - skipping quiz submission");
      setIsQuizModalOpen(false);
      setCurrentQuiz(null);
      setCurrentModule(null);
      return;
    }

    // For regular users, handle quiz completion
    if (currentQuiz && currentModule) {
      const attemptData = {
        quizId: currentQuiz.id,
        score: score,
        isPassed: isPassed,
        answers: answers
      };
      console.log("Submitting quiz attempt:", attemptData);
      console.log("Quiz ID type:", typeof currentQuiz.id, "Value:", currentQuiz.id);
      
      createQuizAttemptMutation.mutate(attemptData);

      if (isPassed) {
        console.log("Quiz passed - marking module as completed");
        // Mark module as completed
        updateProgressMutation.mutate({
          moduleId: currentModule.id,
          isCompleted: true,
          timeSpent: 600, // 10 minutes for quiz completion
        });
      }
    } else {
      console.error("Missing currentQuiz or currentModule:", { currentQuiz, currentModule });
    }

    setIsQuizModalOpen(false);
    setCurrentQuiz(null);
    setCurrentModule(null);
  };

  // Handler for edit module (admin only)
  const handleEditModule = (moduleId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.location.href = `/add-training-module?edit=${moduleId}`;
  };

  // Handler for delete module (admin only)
  const handleDeleteModule = (module: any, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const confirmed = window.confirm(
      `Are you sure you want to delete the training module "${module.title}"? This action cannot be undone.`
    );
    
    if (confirmed) {
      deleteModuleMutation.mutate(module.id);
    }
  };

  // Handler for toggle module status (admin only)
  const handleToggleModuleStatus = (module: any, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const newStatus = module.isActive === false;
    const action = newStatus ? 'activate' : 'deactivate';
    
    const confirmed = window.confirm(
      `Are you sure you want to ${action} the training module "${module.title}"?${
        !newStatus ? ' Inactive modules will not be visible to employees.' : ''
      }`
    );
    
    if (confirmed) {
      toggleModuleStatusMutation.mutate({ moduleId: module.id, isActive: newStatus });
    }
  };

  const handleModuleClick = (module: any) => {
    console.log("Module clicked:", module);
    console.log("Module type:", module.mtype);
    
    // Check if module is available (progressive unlocking)
    const allModules = (modules as any)?.modules || [];
    const isAvailable = isModuleAvailable(module, allModules, progress);
    
    if (!isAvailable) {
      // Show locked modal
      const previousModule = getPreviousModule(module, allModules);
      setLockedModuleInfo({ current: module, previous: previousModule });
      setIsLockedModalOpen(true);
      return;
    }
    
    switch (module.mtype) {
      case "video":
        console.log("Opening video modal for:", module.title);
        setIsVideoOpen(true);
        const moduleWithStartTime = { ...module, startTime: Date.now() };
        console.log("Setting current module with startTime:", moduleWithStartTime);
        setCurrentModule(moduleWithStartTime);
        break;
      case "image":
        console.log("Opening image modal for:", module.title);
        setIsImageOpen(true);
        setCurrentModule(module);
        break;
      case "form":
        console.log("Opening form for:", module.title);
        if (module.externalUrl) {
          window.open(module.externalUrl, '_blank');
          // Mark form modules as completed when opened
          updateProgressMutation.mutate({
            moduleId: module.id,
            isCompleted: true,
            timeSpent: 120, // 2 minutes for form completion
          });
        }
        break;
      case "helpdesk":
        console.log("Opening helpdesk for:", module.title);
        setIsHelpdeskOpen(true);
        // Mark helpdesk modules as completed when opened
        updateProgressMutation.mutate({
          moduleId: module.id,
          isCompleted: true,
          timeSpent: 180, // 3 minutes for helpdesk review
        });
        break;
      default:
        // Handle other module types
        console.log("Module type not handled:", module.mtype);
        // Mark other modules as completed when accessed
        updateProgressMutation.mutate({
          moduleId: module.id,
          isCompleted: true,
          timeSpent: 60, // Default 1 minute
        });
    }
  };

  // Add completion status and availability to modules
  const allModules = (modules as any)?.modules || [];
  
  // Get unique days that actually have modules (sorted)
  const availableDays = [...new Set(allModules.map((m: any) => m.day))]
    .filter((day): day is number => typeof day === 'number')
    .sort((a, b) => a - b);
  
  // Filter modules by selected day and sort by sequence
  const filteredModules = allModules
    .filter((module: any) => module.day === selectedDay)
    .sort((a: any, b: any) => parseInt(a.sequence) - parseInt(b.sequence)) || [];
  console.log('All modules:', allModules);
  console.log('Filtered modules for day', selectedDay, ':', filteredModules);
  console.log('Progress data:', progress);
  
  // Debug: Check if modules have the expected structure
  if (filteredModules.length > 0) {
    console.log('First module structure:', filteredModules[0]);
    console.log('Module day type:', typeof filteredModules[0].day);
    console.log('Module sequence type:', typeof filteredModules[0].sequence);
  }
  
  const modulesWithProgress = filteredModules.map((module: any) => {
    const progressItem = progress?.find((p: any) => p.moduleId === module.id);
    console.log(`Processing module: ${module.title}, day: ${module.day}, sequence: ${module.sequence}`);
    const isAvailable = isModuleAvailable(module, allModules, progress);
    console.log(`Module ${module.title} progress:`, progressItem, 'available:', isAvailable);
    return {
      ...module,
      isCompleted: progressItem?.isCompleted || false,
      isAvailable: isAvailable,
    };
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servify-purple mx-auto mb-4"></div>
          <p className="text-servify-medium-grey">Loading training modules...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="knowledge-hub-page">
        {/* Header Section */}
        <div>
          <h1 className="text-3xl font-bold text-servify-dark-grey" data-testid="text-page-title">Knowledge Hub</h1>
          <p className="text-servify-medium-grey mt-1" data-testid="text-page-description">
            Complete all items to finish your induction program
          </p>
        </div>

        {/* Day Navigation Tabs with Completion Status */}
        {availableDays.length > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex space-x-2">
              {availableDays.map((day) => (
                <Button
                  key={day}
                  type="button"
                  variant={selectedDay === day ? "default" : "outline"}
                  className={`${
                    selectedDay === day 
                      ? 'bg-servify-purple text-white hover:bg-servify-purple-dark' 
                      : 'bg-white text-servify-medium-grey border-servify-light-grey hover:bg-servify-white-grey'
                  } rounded-lg px-6 py-2`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDayChange(day);
                  }}
                  data-testid={`button-day-${day}`}
                >
                  Day {day}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Modules List */}
        <div className="space-y-4" data-testid="modules-list">
          {modulesWithProgress.map((module: any) => (
            <Card 
              key={module.id} 
              className={`transition-all duration-200 shadow-sm ${
                module.isAvailable 
                  ? 'hover:shadow-md cursor-pointer bg-white' 
                  : 'cursor-not-allowed bg-servify-white-grey opacity-75'
              } ${
                // Admin visual indicators for inactive modules
                isAdmin && module.isActive === false 
                  ? 'border border-orange-200 bg-orange-50/30' 
                  : 'border-0'
              } rounded-lg`}
              onClick={() => handleModuleClick(module)}
              data-testid={`module-card-${module.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    {/* Module Icon */}
                    <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${
                      !module.isAvailable 
                        ? 'bg-servify-light-grey' 
                        : module.isCompleted 
                          ? 'bg-servify-green' 
                          : 'bg-servify-purple'
                    }`}>
                      {getModuleIcon(module, !module.isAvailable)}
                    </div>
                    
                    {/* Module Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h2 className={`text-xl font-semibold ${
                          !module.isAvailable ? 'text-servify-medium-grey' : 'text-servify-dark-grey'
                        }`} data-testid={`text-module-title-${module.id}`}>
                          {module.title}
                        </h2>
                        {!module.isAvailable && (
                          <span className="px-2 py-1 bg-servify-light-grey/30 text-servify-medium-grey text-xs font-medium rounded-full">
                            Locked
                          </span>
                        )}
                        {module.isCompleted && module.isAvailable && (
                          <span className="px-2 py-1 bg-servify-green/10 text-servify-green text-xs font-medium rounded-full">
                            Completed
                          </span>
                        )}
                        {/* Admin-only status indicator */}
                        {isAdmin && (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            module.isActive !== false 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {module.isActive !== false ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </div>
                      
                      <p className="text-servify-medium-grey mb-2" data-testid={`text-module-description-${module.id}`}>
                        {module.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Admin Controls and Arrow Icon */}
                  <div className="flex items-center space-x-2">
                    {/* Always show edit buttons for admin users - allow editing everything */}
                    {isAdmin && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleEditModule(module.id, e)}
                          className="px-3 py-2 bg-servify-alt-green/10 border-servify-alt-green/30 text-servify-alt-green hover:bg-servify-alt-green/20 hover:border-servify-alt-green/50 font-medium"
                          data-testid={`button-edit-module-${module.id}`}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleToggleModuleStatus(module, e)}
                          className={`px-3 py-2 font-medium ${
                            module.isActive !== false 
                              ? 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100 hover:border-orange-300'
                              : 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100 hover:border-green-300'
                          }`}
                          disabled={toggleModuleStatusMutation.isPending}
                          data-testid={`button-toggle-module-${module.id}`}
                        >
                          {module.isActive !== false ? (
                            <>
                              <PowerOff className="h-4 w-4 mr-1" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <Power className="h-4 w-4 mr-1" />
                              Activate
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleDeleteModule(module, e)}
                          className="px-3 py-2 bg-servify-purple/10 border-servify-purple/30 text-servify-purple hover:bg-servify-purple/20 hover:border-servify-purple/50 font-medium"
                          data-testid={`button-delete-module-${module.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                    <ArrowRight className="text-servify-medium-grey h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        
          {modulesWithProgress.length === 0 && (
            <Card className="p-8 text-center" data-testid="no-modules-message">
              <div className="max-w-md mx-auto">
                <PlayCircle className="h-16 w-16 text-servify-light-grey mx-auto mb-4" />
                <h3 className="text-lg font-medium text-servify-dark-grey mb-2">No Knowledge Hub Modules for Day {selectedDay}</h3>
                <p className="text-servify-medium-grey">
                  Knowledge hub modules for this day will appear here once they are created by administrators.
                </p>
              </div>
            </Card>
          )}
        </div>

        {/* Video Modal */}
        {isVideoOpen && currentModule && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
              {/* Modal Header */}
              <div className="bg-servify-purple text-white p-6 relative">
                <h2 className="text-2xl font-bold text-center">{currentModule.title}</h2>
                <p className="text-servify-purple/20 text-center mt-2">{currentModule.description}</p>
                
                {/* Close Button */}
                <button
                  onClick={() => setIsVideoOpen(false)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Video Content */}
              <div className="p-6 flex flex-col items-center">
                <video
                  controls
                  className="w-full h-auto max-w-3xl rounded-xl shadow-lg no-seek-video"
                  autoPlay
                  playsInline
                  onEnded={handleVideoComplete}
                  onTimeUpdate={(e) => {
                    // Track video progress but don't trigger completion here
                    // Completion is handled by onEnded event
                  }}
                  onSeeking={(e) => {
                    // Prevent seeking/scrubbing by resetting to last valid time
                    e.preventDefault();
                    const video = e.currentTarget;
                    if (video.dataset.lastTime) {
                      video.currentTime = parseFloat(video.dataset.lastTime);
                    }
                  }}
                  onSeeked={(e) => {
                    // Prevent seeking/scrubbing by resetting to last valid time
                    e.preventDefault();
                    const video = e.currentTarget;
                    if (video.dataset.lastTime) {
                      video.currentTime = parseFloat(video.dataset.lastTime);
                    }
                  }}
                  onTimeUpdateCapture={(e) => {
                    // Store the current valid time for seeking prevention
                    const video = e.currentTarget;
                    video.dataset.lastTime = video.currentTime.toString();
                  }}
                  onError={(e) => {
                    const video = e.target as HTMLVideoElement;
                    console.error("Video failed to load:", video.src);
                    console.log("Module data:", currentModule);
                  }}
                  onLoadedData={(e) => {
                    const video = e.currentTarget;
                    video.play().catch(error => {
                      console.log("Autoplay failed:", error);
                    });
                  }}
                  data-testid="module-video"
                >
                  <source src={currentModule.externalUrl || currentModule.mediaFile || ""} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        )}

        {/* Image Modal */}
        {isImageOpen && currentModule && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
              {/* Modal Header */}
              <div className="bg-servify-purple text-white p-6 relative">
                <h2 className="text-2xl font-bold text-center">{currentModule.title}</h2>
                <p className="text-servify-purple/20 text-center mt-2">{currentModule.description}</p>
                
                {/* Close Button */}
                <button
                  onClick={() => {
                    setIsImageOpen(false);
                    // Mark image as completed when modal is closed
                    if (currentModule) {
                      updateProgressMutation.mutate({
                        moduleId: currentModule.id,
                        isCompleted: true,
                        timeSpent: 60, // 1 minute for viewing image
                      });
                    }
                  }}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Image Content */}
              <div className="p-6 flex flex-col items-center">
                <img
                  src={currentModule.externalUrl || currentModule.mediaFile || ""}
                  alt={currentModule.title}
                  className="w-full h-auto max-w-3xl shadow-lg"
                  data-testid="module-image"
                  onError={(e) => {
                    const img = e.target as HTMLImageElement;
                    console.error("Image failed to load:", img.src);
                    console.log("Module data:", currentModule);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* MCQ Modal */}
        {isMCQOpen && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden relative">
              {/* Modal Header */}
              <div className="bg-servify-purple text-white p-6 relative">
                <h2 className="text-2xl font-bold text-center">Code of Conduct Knowledge Check</h2>
                <p className="text-servify-purple/20 text-center mt-2">Test your understanding of the Code of Conduct</p>
                
                {/* Close Button */}
                <button
                  onClick={() => {
                    setIsMCQOpen(false);
                    setMcqAnswers([]);
                    setCurrentModule(null);
                  }}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* MCQ Content */}
              <div className="p-8">
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Whom does the Code of Conduct apply to? Click all options that apply.
                  </h3>                
                  <div className="space-y-3">
                    {['Employees', 'Consultants', 'Partners', 'Customers'].map((option) => (
                      <label key={option} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border-2 hover:bg-gray-50 transition-colors">
                        <input
                          type="checkbox"
                          checked={mcqAnswers.includes(option)}
                          onChange={() => handleMCQOptionToggle(option)}
                          className="w-5 h-5 text-servify-purple border-gray-300 rounded focus:ring-servify-purple/20"
                        />
                        <span className="text-gray-900 font-medium">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setIsMCQOpen(false);
                      setMcqAnswers([]);
                      setCurrentModule(null);
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMCQSubmit}
                    className="px-6 py-2 bg-servify-purple text-white rounded-lg hover:bg-servify-purple-dark transition-colors"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Refer A Buddy Quiz Modal */}
        {isReferBuddyOpen && (() => {
          const quizzesArray = Array.isArray(quizzesData) ? quizzesData : (quizzesData?.quizzes || []);
          const referBuddyQuiz = quizzesArray.find((q: any) =>
            q.title === "Go Refer A Buddy (GRAB Policy) Quiz"
          );
          const question = referBuddyQuiz?.questions?.[0];
          
          return (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden relative">
                {/* Modal Header */}
                <div className="bg-servify-purple text-white p-6 relative">
                  <h2 className="text-2xl font-bold text-center">Refer A Buddy Quiz</h2>
                  <p className="text-servify-purple/20 text-center mt-2">Test your understanding of the Refer A Buddy policy</p>
                  
                  {/* Close Button */}
                  <button
                    onClick={() => {
                      setIsReferBuddyOpen(false);
                      setMcqAnswers([]);
                      setCurrentModule(null);
                    }}
                    className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                  >
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L6 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Quiz Content */}
                <div className="p-8">
                  <div className="mb-6">
                    {question ? (
                      <>
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">
                          {question.question}
                        </h3>                
                        <div className="space-y-3">
                          {question.options.map((option: string, index: number) => (
                            <label key={option} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border-2 hover:bg-gray-50 transition-colors">
                              <input
                                type="checkbox"
                                checked={mcqAnswers.includes(option)}
                                onChange={() => handleMCQOptionToggle(option)}
                                className="w-5 h-5 text-servify-purple border-gray-300 rounded focus:ring-servify-purple/20"
                              />
                              <span className="text-gray-900 font-medium">{option}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className="text-servify-purple">Quiz data not found</p>
                    )}
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setIsReferBuddyOpen(false);
                        setMcqAnswers([]);
                        setCurrentModule(null);
                      }}
                      className="px-6 py-2 border border-servify-light-grey text-servify-medium-grey rounded-lg hover:bg-servify-white-grey transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        // Handle Refer A Buddy quiz submission
                        const quizzesArray = Array.isArray(quizzesData) ? quizzesData : (quizzesData?.quizzes || []);
                        const referBuddyQuiz = quizzesArray.find((q: any) =>
                          q.title === "Go Refer A Buddy (GRAB Policy) Quiz"
                        );
                        
                        if (referBuddyQuiz) {
                          const question = referBuddyQuiz.questions[0];
                          const correctAnswers = question.correctAnswer.map((index: number) => question.options[index]);
                          const isCorrect = correctAnswers.every((answer: string) => mcqAnswers.includes(answer)) &&
                                           mcqAnswers.length === correctAnswers.length;
                          
                          if (isCorrect) {
                            toast({
                              title: "Quiz Passed! 🎉",
                              description: "Congratulations! You've successfully completed the Refer A Buddy quiz.",
                            });
                            
                            // Mark Refer A Buddy as completed
                            if (currentModule) {
                              updateProgressMutation.mutate({
                                moduleId: currentModule.id,
                                isCompleted: true,
                                timeSpent: 600,
                              });
                            }
                            
                            setIsReferBuddyOpen(false);
                            setMcqAnswers([]);
                            setCurrentModule(null);
                          } else {
                            toast({
                              title: "Quiz Failed",
                              description: "Please review the Refer A Buddy policy and try again.",
                              variant: "destructive",
                            });
                          }
                        }
                      }}
                      className="px-6 py-2 bg-servify-purple text-white rounded-lg hover:bg-servify-purple-dark transition-colors"
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Helpdesk Modal */}
        {isHelpdeskOpen && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
              {/* Modal Header */}
              <div className="bg-servify-purple text-white p-6 relative">
                <h2 className="text-2xl font-bold text-center">Helpdesk Guide</h2>
                <p className="text-servify-purple/20 text-center mt-2">Here's a quick and easy guide to our Servify Apps Helpdesk — so you know exactly whom to reach out to.</p>
                
                {/* Close Button */}
                <button
                  onClick={() => {
                    setIsHelpdeskOpen(false);
                    // Mark helpdesk as completed when modal is closed
                    if (currentModule) {
                      updateProgressMutation.mutate({
                        moduleId: currentModule.id,
                        isCompleted: true,
                        timeSpent: 180, // 3 minutes for helpdesk review
                      });
                    }
                  }}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Helpdesk Content */}
              <div className="p-6 max-h-[70vh] overflow-y-auto">
                <div className="space-y-8">
                  {/* HR Support Section */}
                  <div className="bg-servify-white-grey rounded-xl p-6">
                    <h3 className="text-xl font-bold text-servify-purple mb-4 flex items-center">
                      <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      HR Support
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-light-grey/30">
                        <span className="font-semibold text-servify-dark-grey">Servify LaunchPad</span>
                        <a href="mailto:poonam.m@servify.com" className="text-servify-purple hover:underline font-medium">poonam.m@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-light-grey/30">
                        <span className="font-semibold text-servify-dark-grey">Oracle HCM</span>
                        <a href="mailto:radhika.j@servify.com" className="text-servify-purple hover:underline font-medium">radhika.j@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-light-grey/30">
                        <span className="font-semibold text-servify-dark-grey">Zoho Expense</span>
                        <a href="mailto:shrutika.c@servify.com" className="text-servify-purple hover:underline font-medium">shrutika.c@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-light-grey/30">
                        <span className="font-semibold text-servify-dark-grey">ADP</span>
                        <a href="mailto:apeksha.m@servify.com" className="text-servify-purple hover:underline font-medium">apeksha.m@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-light-grey/30">
                        <span className="font-semibold text-servify-dark-grey">TAS</span>
                        <a href="mailto:abhishek.j@servify.com" className="text-servify-purple hover:underline font-medium">abhishek.j@servify.com</a>
                      </div>
                    </div>
                  </div>

                  {/* IT Support Section */}
                  <div className="bg-servify-white-grey rounded-xl p-6">
                    <h3 className="text-xl font-bold text-servify-purple mb-4 flex items-center">
                      <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      IT Support
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-light-grey/30">
                        <span className="font-semibold text-servify-dark-grey">General IT Issues</span>
                        <a href="mailto:it-support@servify.com" className="text-servify-purple hover:underline font-medium">it-support@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-light-grey/30">
                        <span className="font-semibold text-servify-dark-grey">Slack Support</span>
                        <a href="https://servify.slack.com/archives/C01EPF1MJTU" target="_blank" rel="noopener noreferrer" className="text-servify-purple hover:underline font-medium">servify.slack.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-light-grey/30">
                        <span className="font-semibold text-servify-dark-grey">IT Helpdesk</span>
                        <a href="mailto:it_helpdesk@servify.com" className="text-servify-purple hover:underline font-medium">it_helpdesk@servify.com</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quiz Modal for Admin and Regular Users */}
        <QuizModal
          isOpen={isQuizModalOpen}
          onClose={() => {
            setIsQuizModalOpen(false);
            setCurrentQuiz(null);
            setCurrentModule(null);
          }}
          quiz={currentQuiz}
          isAdmin={isAdmin}
          onQuizComplete={handleQuizComplete}
        />

        {/* Locked Module Modal */}
        <Dialog open={isLockedModalOpen} onOpenChange={setIsLockedModalOpen}>
          <DialogContent className="sm:max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-servify-dark-grey">
                <Lock className="h-5 w-5 text-servify-purple" />
                Module Locked
              </DialogTitle>
              <DialogDescription className="text-servify-medium-grey">
                Complete the previous module to unlock this one.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {lockedModuleInfo?.previous && (
                <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg">
                  <h4 className="font-semibold text-green-800 mb-2">📚 Complete this first:</h4>
                  <p className="text-green-700 font-medium">{lockedModuleInfo.previous.title}</p>
                </div>
              )}
              
              <div className="p-4 bg-gray-50 border-l-4 border-gray-300 rounded-r-lg">
                <h4 className="font-semibold text-gray-700 mb-2">🔒 Locked Module:</h4>
                <p className="text-gray-600">{lockedModuleInfo?.current?.title}</p>
              </div>
              
              <div className="flex justify-end pt-2">
                <Button 
                  onClick={() => setIsLockedModalOpen(false)}
                  className="bg-servify-purple hover:bg-servify-purple-dark text-white"
                  data-testid="button-close-locked-modal"
                >
                  Got it
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
    </div>
  );
}
